// Boneyard landing sections: AJAX add-to-cart wired into Horizon's cart drawer, click-to-load
// YouTube (no player JS until tapped), product gallery thumbs, qty stepper, mobile sticky buy bar.
import { CartLinesUpdateEvent } from '@shopify/events';

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

async function addToCart(form) {
  const btn = form.querySelector('[type=submit]');
  const err = form.querySelector('.by-form__error');
  const fd = new FormData(form);
  const id = fd.get('id');
  const quantity = Number(fd.get('quantity') || 1);
  const sections = cartSectionIds();
  if (err) err.hidden = true;
  btn?.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(window.Shopify?.routes?.root ? `${Shopify.routes.root}cart/add.js` : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity }], sections: sections.join(',') }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'by-landing', itemCount: quantity, sections: data.sections, didError: false },
    });
    if (btn) {
      const label = btn.innerHTML;
      btn.textContent = 'Added to cart';
      setTimeout(() => (btn.innerHTML = label), 2200);
    }
    // No drawer on the page (e.g. cart_type = page): go to the cart.
    if (!document.querySelector('cart-drawer-component')) window.location.href = '/cart';
  } catch (e) {
    deferred.reject(e);
    if (err) {
      err.textContent = e.message;
      err.hidden = false;
    }
  } finally {
    btn?.removeAttribute('aria-busy');
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target.closest?.('form[data-by-cart]');
  if (!form) return;
  e.preventDefault();
  addToCart(form);
});

document.addEventListener('click', (e) => {
  // Click-to-load YouTube
  const yt = e.target.closest?.('.by-yt[data-yt]');
  if (yt && !yt.querySelector('iframe')) {
    const start = yt.dataset.start ? `&start=${yt.dataset.start}` : '';
    const f = document.createElement('iframe');
    f.src = `https://www.youtube-nocookie.com/embed/${yt.dataset.yt}?autoplay=1&rel=0&playsinline=1${start}`;
    f.title = yt.dataset.title || 'YouTube video';
    f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    f.referrerPolicy = 'strict-origin-when-cross-origin';
    f.allowFullscreen = true;
    yt.append(f);
    return;
  }
  // Gallery thumbs
  const th = e.target.closest?.('.by-gallery__thumbs button');
  if (th) {
    const g = th.closest('.by-gallery');
    const main = g.querySelector('.by-gallery__main img');
    main.src = th.dataset.src;
    main.srcset = th.dataset.srcset || '';
    main.alt = th.dataset.alt || '';
    g.querySelectorAll('.by-gallery__thumbs button').forEach((b) => b.setAttribute('aria-current', String(b === th)));
    return;
  }
  // Qty stepper
  const q = e.target.closest?.('.by-qty button');
  if (q) {
    const input = q.parentElement.querySelector('input');
    const v = Math.max(1, Number(input.value || 1) + Number(q.dataset.step));
    input.value = v;
    return;
  }
  // Sticky bar button: submit the section's main buy form (same variant + qty the shopper picked)
  const go = e.target.closest?.('[data-by-sticky-go]');
  if (go) {
    const row = document.getElementById(go.closest('[data-by-sticky-for]').getAttribute('data-by-sticky-for'));
    const form = row?.closest('form[data-by-cart]');
    if (form) form.requestSubmit();
  }
});

// Mobile sticky buy bar: show once the main buy button has scrolled up out of view.
// Scroll-position based (not IntersectionObserver) so a jump straight back to the top hides it too.
// Horizon scrolls .page-wrapper on desktop, so listen in the capture phase for any scroller.
document.querySelectorAll('[data-by-sticky-for]').forEach((bar) => {
  const target = document.getElementById(bar.getAttribute('data-by-sticky-for'));
  if (!target) return;
  let queued = false;
  const update = () => {
    queued = false;
    const show = target.getBoundingClientRect().bottom < 0;
    bar.dataset.show = String(show);
    bar.setAttribute('aria-hidden', String(!show));
    bar.querySelector('[data-by-sticky-go]')?.setAttribute('tabindex', show ? '0' : '-1');
  };
  const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
});

// Variant pills: swap price, button state, main image, sticky price and the URL (?variant=) on change
document.addEventListener('change', (e) => {
  const input = e.target.closest?.('[data-by-variants] input[name=id]');
  if (!input) return;
  const fs = input.closest('[data-by-variants]');
  const data = JSON.parse(fs.querySelector('[data-by-variant-json]').textContent);
  const v = data.find((x) => String(x.id) === input.value);
  if (!v) return;
  const section = fs.closest('section.by');
  fs.querySelector('[data-by-variant-label]').textContent = v.title;
  const price = section.querySelector('[data-by-price]');
  if (price) price.innerHTML = v.price_html;
  const btn = fs.closest('form').querySelector('[data-by-submit]');
  if (btn) {
    btn.disabled = !v.available;
    btn.textContent = v.available ? btn.dataset.label : 'Sold out';
  }
  const sticky = section.querySelector('[data-by-sticky-price]');
  if (sticky) sticky.textContent = v.price;
  if (v.image) {
    const main = section.querySelector('.by-gallery__main img');
    if (main) { main.src = v.image; main.srcset = ''; main.alt = v.image_alt || ''; }
  }
  const url = new URL(location.href);
  url.searchParams.set('variant', v.id);
  history.replaceState(history.state, '', url);
});


// Product descriptions come from admin and can contain dead image links: hide any that fail to load.
const hideBroken = (img) => { if (img.complete && img.naturalWidth === 0) img.hidden = true; };
document.querySelectorAll('.by-rte img').forEach((img) => {
  hideBroken(img);
  img.addEventListener('error', () => (img.hidden = true), { once: true });
});
